import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { RadioGroupItemWithLabel } from "@/components/custom/RadioGroupItemWithLabel";
import { RadioGroup } from "@/components/ui/radio-group";
import { trackSizeSelected } from "@/lib/analytics/segment";
import { filterSizeOptionsByConstraints, getSizeConstraints } from "@/lib/product/size";
import { useProduct } from "@/queries/useProduct";
import { usePdpStore } from "@/stores/usePdpStore";
import CustomSize from "./CustomSize";
import OptionLabel from "./OptionLabel";
import { useProductBuilderContext } from "./ProductBuilderContext";
import { useProductMicrocopy } from "./useProductMicrocopy";
import ValidationError, { useFieldErrorId } from "./ValidationError";
interface SizeSelection {
  isCustom: boolean;
  width: string | undefined;
  height: string | undefined;
  presetValues: Set<string>;
}
function getSelectedValue({
  isCustom,
  width,
  height,
  presetValues
}: SizeSelection): string {
  if (isCustom) return "custom";
  if (width && height) {
    const key = `${width}x${height}`;
    return presetValues.has(key) ? key : "";
  }
  return "";
}
function getCustomSizeLabel(isCustom: boolean, width?: string, height?: string): string {
  if (isCustom && width && height) return `Custom Size: ${width}x${height}`;
  return "Custom Size";
}
interface CustomInitialArgs {
  customPrefill: {
    width: string;
    height: string;
  } | undefined;
  hasNonPresetSize: boolean;
  width: string | undefined;
  height: string | undefined;
}
function getCustomInitial({
  customPrefill,
  hasNonPresetSize,
  width,
  height
}: CustomInitialArgs): {
  width?: string;
  height?: string;
} {
  if (customPrefill) return customPrefill;
  if (hasNonPresetSize) return {
    width,
    height
  };
  return {};
}
interface NonPresetSizeArgs {
  allowCustomSize: boolean;
  presetValues: Set<string>;
  width: string | undefined;
  height: string | undefined;
}
function isNonPresetSize({
  allowCustomSize,
  presetValues,
  width,
  height
}: NonPresetSizeArgs): boolean {
  if (!allowCustomSize || !width || !height) return false;
  return !presetValues.has(`${width}x${height}`);
}
export default function Size() {
  const $ = _c(38);
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const selectedShape = usePdpStore(_temp);
  const selectedMaterial = usePdpStore(_temp2);
  const width = usePdpStore(_temp3);
  const height = usePdpStore(_temp4);
  const setWidth = usePdpStore(_temp5);
  const setHeight = usePdpStore(_temp6);
  const selectSize = usePdpStore(_temp7);
  const clearValidationError = usePdpStore(_temp8);
  const sizeErrorId = useFieldErrorId("size");
  const shapeValue = selectedShape ?? productAdmin?.defaultShape;
  const selectedShapeOption = productAdmin?.shapeOptions.find(shape => shape.sku === shapeValue);
  const materialValue = selectedMaterial ?? productAdmin?.defaultMaterial;
  const materialOption = productAdmin?.materialOptions?.find(mat => mat.sku === materialValue);
  const constraints = getSizeConstraints(materialOption, productAdmin);
  const sizeOptions = filterSizeOptionsByConstraints(selectedShapeOption?.sizeOptions ?? [], constraints).toSorted(_temp9);
  const presetValues = new Set(sizeOptions.map(_temp10));
  const allowCustomSize = !!productAdmin?.allowCustomSize;
  const hasNonPresetSize = isNonPresetSize({
    allowCustomSize,
    presetValues,
    width,
    height
  });
  const [customSelected, setCustomSelected] = useState(false);
  const [customPrefill, setCustomPrefill] = useState(undefined);
  const microcopyFields = useProductMicrocopy();
  const sizeHelper = microcopyFields?.sizeHelper;
  const sizeLabel = microcopyFields?.sizeLabel ?? "Size";
  const sizeControlIdBase = `size-${product.sku}`;
  const sizeLabelId = `${sizeControlIdBase}-label`;
  const sizeHelpDialogId = `${sizeControlIdBase}-help-dialog`;
  const isCustom = allowCustomSize && (customSelected || hasNonPresetSize);
  const selectedValue = getSelectedValue({
    isCustom,
    width,
    height,
    presetValues
  });
  let t0;
  if ($[0] !== height || $[1] !== setHeight || $[2] !== setWidth || $[3] !== width) {
    t0 = function selectCustom() {
      if (width && height) {
        setCustomPrefill({
          width,
          height
        });
        setWidth(undefined);
        setHeight(undefined);
      }
      setCustomSelected(true);
      trackSizeSelected({
        value: "custom"
      });
    };
    $[0] = height;
    $[1] = setHeight;
    $[2] = setWidth;
    $[3] = width;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const selectCustom = t0;
  let t1;
  if ($[5] !== clearValidationError || $[6] !== selectSize) {
    t1 = function selectPreset(value) {
      setCustomSelected(false);
      setCustomPrefill(undefined);
      const [w, h] = value.split("x");
      selectSize(w, h);
      clearValidationError("size");
      trackSizeSelected({
        value,
        width: w,
        height: h
      });
    };
    $[5] = clearValidationError;
    $[6] = selectSize;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const selectPreset = t1;
  let t2;
  if ($[8] !== selectCustom || $[9] !== selectPreset) {
    t2 = function handleSizeChange(value_0) {
      if (value_0 === "custom") {
        selectCustom();
      } else {
        selectPreset(value_0);
      }
    };
    $[8] = selectCustom;
    $[9] = selectPreset;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleSizeChange = t2;
  const handleCustomApplied = function handleCustomApplied(appliedWidth, appliedHeight) {
    if (!presetValues.has(`${appliedWidth}x${appliedHeight}`)) {
      return;
    }
    setCustomSelected(false);
    setCustomPrefill(undefined);
  };
  const customInitial = getCustomInitial({
    customPrefill,
    hasNonPresetSize,
    width,
    height
  });
  if (sizeOptions.length === 0) {
    return null;
  }
  const t3 = "flex flex-col gap-1";
  const t4 = "size";
  let t5;
  if ($[11] !== sizeHelpDialogId || $[12] !== sizeHelper || $[13] !== sizeLabel || $[14] !== sizeLabelId) {
    t5 = <OptionLabel id={sizeLabelId} label={sizeLabel} helper={sizeHelper} helpDialogId={sizeHelpDialogId} />;
    $[11] = sizeHelpDialogId;
    $[12] = sizeHelper;
    $[13] = sizeLabel;
    $[14] = sizeLabelId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const T0 = RadioGroup;
  const t6 = "true";
  const t7 = "grid w-full grid-cols-2 gap-4 min-[500px]:grid-cols-3";
  let t8;
  if ($[16] !== sizeControlIdBase) {
    t8 = option => {
      const sizeValue = `${option.width}x${option.height}`;
      return <RadioGroupItemWithLabel key={sizeValue} value={sizeValue} id={`${sizeControlIdBase}-${sizeValue}`}>{sizeValue}</RadioGroupItemWithLabel>;
    };
    $[16] = sizeControlIdBase;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = sizeOptions.map(t8);
  const t10 = allowCustomSize && <RadioGroupItemWithLabel value="custom" id={`${sizeControlIdBase}-custom`} className="col-span-2">{getCustomSizeLabel(hasNonPresetSize, width, height)}</RadioGroupItemWithLabel>;
  let t11;
  if ($[18] !== T0 || $[19] !== handleSizeChange || $[20] !== selectedValue || $[21] !== sizeErrorId || $[22] !== sizeLabelId || $[23] !== t10 || $[24] !== t9) {
    t11 = <T0 aria-labelledby={sizeLabelId} aria-describedby={sizeErrorId} aria-required={t6} value={selectedValue} onValueChange={handleSizeChange} className={t7}>{t9}{t10}</T0>;
    $[18] = T0;
    $[19] = handleSizeChange;
    $[20] = selectedValue;
    $[21] = sizeErrorId;
    $[22] = sizeLabelId;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== customInitial || $[27] !== customSelected || $[28] !== handleCustomApplied || $[29] !== isCustom) {
    t12 = isCustom && <CustomSize initialWidth={customInitial.width} initialHeight={customInitial.height} autoFocus={customSelected} onApplied={handleCustomApplied} />;
    $[26] = customInitial;
    $[27] = customSelected;
    $[28] = handleCustomApplied;
    $[29] = isCustom;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== isCustom) {
    t13 = !isCustom && <ValidationError field="size" />;
    $[31] = isCustom;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t5) {
    t14 = <div className={t3} data-validation-field={t4}>{t5}{t11}{t12}{t13}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t5;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp10(o) {
  return `${o.width}x${o.height}`;
}
function _temp9(a, b) {
  return a.position - b.position;
}
function _temp8(state_6) {
  return state_6.clearValidationError;
}
function _temp7(state_5) {
  return state_5.selectSize;
}
function _temp6(state_4) {
  return state_4.setHeight;
}
function _temp5(state_3) {
  return state_3.setWidth;
}
function _temp4(state_2) {
  return state_2.height;
}
function _temp3(state_1) {
  return state_1.width;
}
function _temp2(state_0) {
  return state_0.material;
}
function _temp(state) {
  return state.shape;
}